import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { ExternalLink, ChevronDown, ChevronUp, Activity, Trash2, RefreshCw, RotateCcw, ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { getPublicProject, getPublicEndpoints, getPublicEndpointScenarios, getPublicScenarioState } from '@/api/public'
import { deleteProjectLogs } from '@/api/logs'
import { resetScenarioState } from '@/api/scenarios'
import { useAuth } from '@/hooks/useAuth'
import { usePublicLogs } from '@/hooks/usePublicLogs'
import { ApiClient } from '@/components/api-client/ApiClient'
import { RequestDocsView, hasRequestDocsContent } from '@/components/endpoint/RequestDocsView'
import { buildApiClientDefaults } from '@/lib/request-docs'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { WEB_BASE_URL, MOCK_BASE_DOMAIN, MOCK_BASE_SCHEME } from '@/lib/constants'
import type { PublicEndpoint, PublicScenario } from '@/api/public'
import type { SequenceConfig } from '@/api/scenarios'
import type { ConnectionStatus } from '@/hooks/useProjectLogs'

// ── Method badge ───────────────────────────────────────────────────────────

const METHOD_COLORS: Record<string, string> = {
  GET:     'bg-blue-100 text-blue-700 border-blue-200',
  POST:    'bg-green-100 text-green-700 border-green-200',
  PUT:     'bg-orange-100 text-orange-700 border-orange-200',
  PATCH:   'bg-purple-100 text-purple-700 border-purple-200',
  DELETE:  'bg-red-100 text-red-700 border-red-200',
  HEAD:    'bg-gray-100 text-gray-600 border-gray-200',
  OPTIONS: 'bg-gray-100 text-gray-600 border-gray-200',
}

function MethodBadge({ method }: { method: string }) {
  return (
    <span className={cn(
      'inline-flex items-center rounded border px-2 py-0.5 text-xs font-mono font-semibold shrink-0',
      METHOD_COLORS[method] ?? 'bg-gray-100 text-gray-600 border-gray-200',
    )}>
      {method}
    </span>
  )
}

// ── Connection indicator ───────────────────────────────────────────────────

function ConnectionIndicator({ status }: { status: ConnectionStatus }) {
  const { t } = useTranslation()
  const label =
    status === 'connected' ? 'LIVE' :
    status === 'connecting' ? t('shared.connecting') :
    t('shared.disconnected')
  const dotClass =
    status === 'connected' ? 'bg-green-500 animate-pulse' :
    status === 'connecting' ? 'bg-yellow-400 animate-pulse' : 'bg-gray-400'
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className={cn('h-2 w-2 rounded-full', dotClass)} />
      {label}
    </span>
  )
}

// ── Sequence counter (public) ──────────────────────────────────────────────

function PublicSequenceCounter({ scenario, isOwner }: { scenario: PublicScenario; isOwner: boolean }) {
  const queryClient = useQueryClient()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const { t } = useTranslation()

  const config = scenario.config as unknown as SequenceConfig
  const stepCount = config.steps?.length ?? 0

  const { data, refetch } = useQuery({
    queryKey: ['public-scenario-state', scenario.id],
    queryFn: () => getPublicScenarioState(scenario.id),
    staleTime: 0,
    refetchInterval: 3000,
  })

  const resetMutation = useMutation({
    mutationFn: () => resetScenarioState(scenario.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['public-scenario-state', scenario.id] })
      toast.success(t('shared.seqResetSuccess'))
    },
    onError: () => toast.error(t('shared.seqResetError')),
  })

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await refetch()
    setIsRefreshing(false)
  }

  const counts = data?.counts ?? {}
  const keys = Object.keys(counts)

  let countLabel: string
  if (keys.length === 0) {
    countLabel = `0 / ${stepCount}`
  } else if (counts['g'] !== undefined) {
    countLabel = `${counts['g']} / ${stepCount}`
  } else {
    countLabel = t('endpoint.activeScopes', { count: keys.length })
  }

  const isEmpty = keys.length === 0

  return (
    <div className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/40">
      <div className="flex items-center gap-2 min-w-0">
        <span className="inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-mono font-semibold bg-violet-100 text-violet-700 border-violet-200 shrink-0">
          SEQ
        </span>
        <span className="text-xs text-muted-foreground truncate">{scenario.name}</span>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <span className={cn('text-xs tabular-nums', isEmpty ? 'text-muted-foreground' : 'text-foreground font-medium')}>
          {countLabel}
        </span>
        <Button
          variant="ghost" size="icon"
          className="h-5 w-5 text-muted-foreground hover:text-blue-500"
          onClick={() => void handleRefresh()}
          disabled={isRefreshing}
          title={t('endpoint.sequence.refreshNow')}
        >
          <RefreshCw className={cn('h-3 w-3', isRefreshing && 'animate-spin')} />
        </Button>
        {isOwner && (
          <Button
            variant="ghost" size="icon"
            className="h-5 w-5 text-muted-foreground hover:text-orange-500 disabled:opacity-30"
            onClick={() => resetMutation.mutate()}
            disabled={resetMutation.isPending || isEmpty}
            title={t('endpoint.sequence.resetCounter')}
          >
            <RotateCcw className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  )
}

// ── Endpoint row ───────────────────────────────────────────────────────────

function EndpointRow({ endpoint, slug, expanded, onToggle, isOwner }: {
  endpoint: PublicEndpoint
  slug: string
  expanded: boolean
  onToggle: () => void
  isOwner: boolean
}) {
  const baseUrl = `${MOCK_BASE_SCHEME}://${slug}.${MOCK_BASE_DOMAIN}`
  const apiDefaults = buildApiClientDefaults(endpoint.path, endpoint.request_docs, baseUrl)
  const { t } = useTranslation()

  const { data: scenarios } = useQuery({
    queryKey: ['public-endpoint-scenarios', slug, endpoint.id],
    queryFn: () => getPublicEndpointScenarios(slug, endpoint.id),
    enabled: expanded,
    staleTime: 30_000,
  })

  const sequenceScenarios = scenarios?.filter(s => s.type === 'sequence' && s.enabled) ?? []

  return (
    <div className="border-b last:border-b-0">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors text-left"
        onClick={onToggle}
      >
        <MethodBadge method={endpoint.method} />
        <span className="font-mono text-sm flex-1 min-w-0 truncate">{endpoint.path}</span>
        {endpoint.description && (
          <span className="text-xs text-muted-foreground hidden sm:block truncate max-w-48">
            {endpoint.description}
          </span>
        )}
        {expanded
          ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
          : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        }
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t bg-muted/20 space-y-3">
          {hasRequestDocsContent(endpoint.request_docs) && (
            <div className="rounded-md border bg-background p-3">
              <RequestDocsView requestDocs={endpoint.request_docs} />
            </div>
          )}
          <ApiClient key={JSON.stringify(endpoint.request_docs)} defaultMethod={endpoint.method} {...apiDefaults} />
          {sequenceScenarios.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-2">
                {t('shared.seqSectionTitle')}
              </p>
              {sequenceScenarios.map(sc => (
                <PublicSequenceCounter key={sc.id} scenario={sc} isOwner={isOwner} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Public log viewer (wraps LogViewer with public hook) ───────────────────

function PublicLogSection({ slug, projectId, isOwner }: { slug: string; projectId: string; isOwner: boolean }) {
  const { logs, isLoading, connectionStatus, loadMore, clearLogs, hasMore } = usePublicLogs(slug, projectId)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [showClearDialog, setShowClearDialog] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const { t } = useTranslation()

  const handleLoadMore = async () => {
    setIsLoadingMore(true)
    try { await loadMore() } finally { setIsLoadingMore(false) }
  }

  const handleClearAll = async () => {
    setIsClearing(true)
    try {
      await deleteProjectLogs(projectId)
      clearLogs()
      setShowClearDialog(false)
      toast.success(t('shared.clearAllSuccess'))
    } catch {
      toast.error(t('shared.clearAllError'))
    } finally {
      setIsClearing(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold">{t('shared.requestLog')}</h2>
          <ConnectionIndicator status={connectionStatus} />
        </div>
        {isOwner && logs.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowClearDialog(true)}
            className="gap-1.5 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t('shared.clearAll')}
          </Button>
        )}
      </div>

      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('shared.clearAllConfirmTitle')}</DialogTitle>
            <DialogDescription>{t('shared.clearAllConfirmDesc')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearDialog(false)} disabled={isClearing}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={() => void handleClearAll()} disabled={isClearing}>
              {isClearing ? t('common.loading') : t('shared.clearAll')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LogViewerTable
        logs={logs}
        isLoading={isLoading}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={() => void handleLoadMore()}
      />
    </div>
  )
}

// ── Inline log table (extracted from LogViewer to avoid projectId coupling) ─

import type { RequestLog } from '@/api/logs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function statusColor(code: number): string {
  if (code >= 500) return 'text-red-600 font-semibold'
  if (code >= 400) return 'text-amber-600 font-semibold'
  if (code >= 300) return 'text-blue-600'
  return 'text-green-600'
}

function prettyJson(value: unknown): string {
  try { return JSON.stringify(value, null, 2) } catch { return String(value) }
}
function tryPrettyJson(s: string): string {
  try { return JSON.stringify(JSON.parse(s), null, 2) } catch { return s }
}

function LogDetailDialog({ log, onClose }: { log: RequestLog; onClose: () => void }) {
  const { t } = useTranslation()

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-mono text-sm font-semibold pr-8">
            <MethodBadge method={log.method} />
            <span className="truncate min-w-0">{log.path}</span>
            <span className={cn('ml-auto shrink-0 text-base', statusColor(log.response_status))}>
              {log.response_status}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            <div><span className="font-medium text-foreground">{t('shared.logTime2')}: </span>{new Date(log.created_at).toLocaleString('ja-JP')}</div>
            <div><span className="font-medium text-foreground">{t('shared.logLatency2')}: </span>{log.latency_ms} ms</div>
          </div>
          {typeof log.query === 'object' && log.query !== null && Object.keys(log.query).length > 0 && (
            <LogSection label={t('shared.queryParams')} value={prettyJson(log.query)} />
          )}
          <LogSection label={t('shared.requestHeaders')} value={prettyJson(log.request_headers)} />
          {log.request_body && <LogSection label={t('shared.requestBody')} value={tryPrettyJson(log.request_body)} />}
          <LogSection label={t('shared.responseHeaders')} value={prettyJson(log.response_headers)} />
          {log.response_body && <LogSection label={t('shared.responseBody')} value={tryPrettyJson(log.response_body)} />}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function LogSection({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
      <pre className="rounded-md bg-muted px-3 py-2 text-xs font-mono whitespace-pre-wrap break-all overflow-auto max-h-48">{value}</pre>
    </div>
  )
}

function LogViewerTable({ logs, isLoading, hasMore, isLoadingMore, onLoadMore }: {
  logs: RequestLog[]
  isLoading: boolean
  hasMore: boolean
  isLoadingMore: boolean
  onLoadMore: () => void
}) {
  const [selectedLog, setSelectedLog] = useState<RequestLog | null>(null)
  const { t } = useTranslation()

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">{t('shared.logTime')}</TableHead>
              <TableHead className="w-24">{t('shared.logMethod')}</TableHead>
              <TableHead>{t('shared.logPath')}</TableHead>
              <TableHead className="w-16 text-center">{t('shared.logStatus')}</TableHead>
              <TableHead className="w-20 text-right">{t('shared.logLatency')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow><TableCell colSpan={5} className="h-20 text-center text-muted-foreground text-sm">{t('shared.loading')}</TableCell></TableRow>
            )}
            {!isLoading && logs.length === 0 && (
              <TableRow><TableCell colSpan={5} className="h-20 text-center text-muted-foreground text-sm">{t('shared.noLogs')}</TableCell></TableRow>
            )}
            {logs.map((log) => (
              <TableRow key={log.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedLog(log)}>
                <TableCell className="font-mono text-xs text-muted-foreground">{formatTime(log.created_at)}</TableCell>
                <TableCell><MethodBadge method={log.method} /></TableCell>
                <TableCell className="font-mono text-xs max-w-xs truncate">
                  {log.path}
                  {typeof log.query === 'object' && log.query !== null && Object.keys(log.query).length > 0 && (
                    <span className="text-muted-foreground">?{new URLSearchParams(log.query as Record<string, string>).toString()}</span>
                  )}
                </TableCell>
                <TableCell className={cn('text-center text-sm', statusColor(log.response_status))}>{log.response_status}</TableCell>
                <TableCell className="text-right text-xs text-muted-foreground">{log.latency_ms} ms</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {hasMore && !isLoading && (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" onClick={onLoadMore} disabled={isLoadingMore}>
            {isLoadingMore ? t('shared.loadingMore') : t('shared.loadMore')}
          </Button>
        </div>
      )}
      {selectedLog && <LogDetailDialog log={selectedLog} onClose={() => setSelectedLog(null)} />}
    </>
  )
}

// ── Guest CTA banner ──────────────────────────────────────────────────────

function GuestCtaBanner() {
  const { t } = useTranslation()
  const landingUrl = WEB_BASE_URL || '/'
  const loginUrl = `${WEB_BASE_URL}/login`

  return (
    <div className="fixed bottom-0 inset-x-0 z-20 border-t bg-background/95 backdrop-blur shadow-lg">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground text-center sm:text-left">
          <span className="font-semibold text-foreground">ScenarioMock</span>
          {' — '}
          {t('shared.ctaBannerText')}
        </p>
        <div className="flex items-center gap-3 shrink-0">
          <a
            href={loginUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
          >
            {t('shared.ctaBannerLogin')}
          </a>
          <Button size="sm" asChild>
            <a href={landingUrl} target="_blank" rel="noopener noreferrer">
              {t('shared.ctaBannerStart')}
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </a>
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────

interface SharedPageProps {
  slug?: string // hostname から渡される場合
}

export function SharedPage({ slug: slugProp }: SharedPageProps) {
  const params = useParams<{ slug: string }>()
  const slug = slugProp ?? params.slug ?? ''
  const { user } = useAuth()
  const { t } = useTranslation()
  const [expandedEndpointId, setExpandedEndpointId] = useState<string | null>(null)

  const { data: project, isLoading: projectLoading, isError: projectError } = useQuery({
    queryKey: ['public-project', slug],
    queryFn: () => getPublicProject(slug),
    enabled: !!slug,
    retry: false,
  })

  const { data: endpoints, isLoading: endpointsLoading } = useQuery({
    queryKey: ['public-endpoints', slug],
    queryFn: () => getPublicEndpoints(slug),
    enabled: !!slug && !!project,
  })

  const isOwner = !!user && !!project && user.id === project.owner_user_id

  function toggleEndpoint(id: string) {
    setExpandedEndpointId(prev => prev === id ? null : id)
  }

  if (projectLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="text-muted-foreground text-sm">{t('shared.loading')}</span>
      </div>
    )
  }

  if (projectError || !project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <p className="text-lg font-semibold">{t('shared.notFound')}</p>
        <p className="text-sm text-muted-foreground">
          {t('shared.notFoundDescription')}
        </p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {!user && <GuestCtaBanner />}
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-sm font-semibold text-muted-foreground shrink-0">ScenarioMock</span>
            <span className="text-muted-foreground">/</span>
            <span className="text-sm font-semibold truncate">{project.name}</span>
          </div>
          {isOwner && (
            <Button asChild variant="outline" size="sm" className="shrink-0">
              <Link to={`/projects/${project.id}`}>
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                {t('shared.adminPanel')}
              </Link>
            </Button>
          )}
        </div>
      </header>

      {/* Body */}
      <main className={cn('mx-auto max-w-4xl px-4 sm:px-6 py-8 space-y-8', !user && 'pb-24')}>

        {/* Project info */}
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">{project.name}</h1>
          {project.description && (
            <p className="text-muted-foreground">{project.description}</p>
          )}
          {project.is_shared && (
            <p className="text-xs text-muted-foreground">{t('shared.galleryResetNote')}</p>
          )}
          <p className="text-xs font-mono text-muted-foreground pt-1">
            Mock URL: <span className="text-foreground">{slug}.{MOCK_BASE_DOMAIN}</span>
          </p>
        </div>

        {/* Endpoint list */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold">{t('shared.endpoints')}</h2>
          <div className="rounded-md border divide-y">
            {endpointsLoading && (
              <div className="py-8 text-center text-sm text-muted-foreground">{t('shared.loading')}</div>
            )}
            {!endpointsLoading && endpoints?.length === 0 && (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {t('shared.noEndpoints')}
              </div>
            )}
            {endpoints?.map((ep) => (
              <EndpointRow
                key={ep.id}
                endpoint={ep}
                slug={slug}
                expanded={expandedEndpointId === ep.id}
                onToggle={() => toggleEndpoint(ep.id)}
                isOwner={isOwner}
              />
            ))}
          </div>
        </div>

        {/* Log viewer */}
        {project.id && (
          <div className="space-y-3">
            <PublicLogSection slug={slug} projectId={project.id} isOwner={isOwner} />
          </div>
        )}

      </main>
    </div>
  )
}
